/**
 * Model bewerken (`/personas/edit/:id`) — gemeten aan oud.
 *
 * Velden zoals het oude formulier ze toont: Name, Stage Name (profile name),
 * Avatar URL met de hint eronder, Active, FanCentro Model ID, f2f.com Model
 * ID, het blok AI Schedule (From / To / Timezone / Slow Mode) en de ruwe
 * Persona Config als JSON. Eén endpoint: models/personas/{id}.
 */
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ErrorNote, Loading, Panel } from '../components/ui'
import { useApi } from '../lib/useApi'
import { useActions, mut } from '../lib/actions'
import { isBeheer, useMij } from '../lib/rechten'

interface Persona {
  id: number
  name: string | null
  stage_name: string | null
  avatar_url: string | null
  active: boolean
  fancentro_model_id: string | null
  f2f_model_id: string | null
  persona_config: Record<string, unknown> | null
}

export default function ModelEdit() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { run, busy } = useActions()
  const p = useApi<Persona>(`/api/admin/models/personas/${id}`, [id])
  const [form, setForm] = useState<Record<string, string | boolean> | null>(null)
  const [cfg, setCfg] = useState<string | null>(null)
  const [cfgError, setCfgError] = useState<string | null>(null)
  const mij = useMij()

  const m = p.data
  // 30-09-2026 (Jordy): de ruwe config en de AI-instellingen zijn voor beheer.
  if (mij.data && !isBeheer(mij.data)) {
    return <ErrorNote error="Only managers and the owner can edit a model." />
  }
  if (p.loading && !p.data) return <Loading />
  if (p.error) return <ErrorNote error={p.error} />
  if (!m) return <ErrorNote error="Model not found." />

  const sched = (m.persona_config?.ai_schedule ?? {}) as Record<string, unknown>
  const v = (k: string, fallback: unknown): string =>
    String(form?.[k] ?? fallback ?? '')
  const active = Boolean(form?.active ?? m.active)
  const set = (k: string, val: string | boolean) =>
    setForm({ ...(form ?? {}), [k]: val })

  const cfgText = cfg ?? JSON.stringify(m.persona_config ?? {}, null, 2)
  const dirty = form !== null || cfg !== null

  function body() {
    let config: unknown = m!.persona_config ?? {}
    if (cfg !== null) config = JSON.parse(cfg)
    const merged = { ...(config as Record<string, unknown>) }
    // De vier roostervelden staan in persona_config, niet als kolom.
    merged.ai_schedule = {
      ...(sched as Record<string, unknown>),
      from: v('from', sched.from),
      to: v('to', sched.to),
      timezone: v('timezone', sched.timezone),
      slow_mode_max_per_day: Number(v('slow', sched.slow_mode_max_per_day)) || 0,
    }
    return {
      name: v('name', m!.name),
      stage_name: v('stage_name', m!.stage_name),
      avatar_url: v('avatar_url', m!.avatar_url),
      active,
      fancentro_model_id: v('fancentro_model_id', m!.fancentro_model_id) || null,
      f2f_model_id: v('f2f_model_id', m!.f2f_model_id) || null,
      persona_config: merged,
    }
  }

  return (
    <>
      <div className="filters">
        <button className="btn ghost sm" type="button" onClick={() => navigate('/personas')}>
          Models
        </button>
        <button className="btn ghost sm" type="button" onClick={() => p.refetch()}>
          Refresh
        </button>
        <div className="spacer" style={{ flex: 1 }} />
        <button
          className="btn primary sm"
          type="button"
          disabled={!dirty || busy === 'Save model'}
          onClick={() => {
            // Ongeldige JSON tegenhouden vóór we iets versturen.
            if (cfg !== null) {
              try { JSON.parse(cfg) } catch (e) {
                setCfgError(e instanceof Error ? e.message : 'Ongeldige JSON')
                return
              }
            }
            setCfgError(null)
            run({
              label: 'Save model',
              request: mut.patch(`/api/admin/models/personas/${id}`, body()),
              onDone: () => { setForm(null); setCfg(null); p.refetch() },
              successText: 'Model saved.',
            })
          }}
        >
          Save
        </button>
      </div>

      <Panel title="Edit Model" desc={dirty ? 'Unsaved changes' : undefined}>
        <div className="field">
          <label>Name</label>
          <input className="inp" value={v('name', m.name)}
                 onChange={(e) => set('name', e.target.value)} />
        </div>
        <div className="field">
          <label>Stage Name (profile name)</label>
          <input className="inp" value={v('stage_name', m.stage_name)}
                 onChange={(e) => set('stage_name', e.target.value)} />
        </div>
        <div className="field">
          <label>Avatar URL</label>
          <input className="inp" value={v('avatar_url', m.avatar_url)}
                 onChange={(e) => set('avatar_url', e.target.value)} />
          <span className="hint">Paste an image URL for the model's profile photo</span>
        </div>
        <label className="chkrow">
          <span className={active ? 'chk on' : 'chk'} onClick={() => set('active', !active)}>
            {active ? '✓' : ''}
          </span>
          Active
        </label>
        <div className="cols" style={{ gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div className="field">
            <label>FanCentro Model ID</label>
            <input className="inp" value={v('fancentro_model_id', m.fancentro_model_id)}
                   onChange={(e) => set('fancentro_model_id', e.target.value)} />
          </div>
          <div className="field">
            <label>f2f.com Model ID</label>
            <input className="inp" value={v('f2f_model_id', m.f2f_model_id)}
                   onChange={(e) => set('f2f_model_id', e.target.value)} />
          </div>
        </div>
      </Panel>

      <Panel title="AI Schedule"
             desc="Outside this window the AI does not reply; Slow Mode caps the number of replies per day.">
        <div className="cols" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14 }}>
          <div className="field">
            <label>From (HH:MM)</label>
            <input className="inp" placeholder="09:00" value={v('from', sched.from)}
                   onChange={(e) => set('from', e.target.value)} />
          </div>
          <div className="field">
            <label>To (HH:MM)</label>
            <input className="inp" placeholder="23:00" value={v('to', sched.to)}
                   onChange={(e) => set('to', e.target.value)} />
          </div>
          <div className="field">
            <label>Timezone</label>
            <input className="inp" placeholder="Europe/Amsterdam"
                   value={v('timezone', sched.timezone)}
                   onChange={(e) => set('timezone', e.target.value)} />
          </div>
          <div className="field">
            <label>Slow Mode (max AI responses per 24h)</label>
            <input className="inp" type="number" min={0}
                   value={v('slow', sched.slow_mode_max_per_day)}
                   onChange={(e) => set('slow', e.target.value)} />
          </div>
        </div>
      </Panel>

      <Panel title="Persona Config (JSON)">
        <textarea
          className="inp mono"
          rows={14}
          value={cfgText}
          onChange={(e) => { setCfg(e.target.value); setCfgError(null) }}
        />
        {cfgError ? (
          <div className="note" style={{ borderColor: 'rgba(255,107,107,.4)', marginTop: 8 }}>
            <span style={{ color: 'var(--bad)' }}>Ongeldige JSON:</span> {cfgError}
          </div>
        ) : null}
      </Panel>
    </>
  )
}
